import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels
} from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getRewards } from '../../../api/rewards.api';
import { RewardCard } from '../../../components/reward/card';
import SectionHeader from '../../../components/ui/section-header';
import {
  REWARD_CHANNEL_LABELS,
  RewardChannel
} from '../../../constants/rewards.const';
import { UIReward } from '../../../types/rewards';

const categories: { name: string; value: 'active' | 'past' }[] = [
  {
    name: 'Active Rewards',
    value: 'active'
  },
  {
    name: 'Past Rewards',
    value: 'past'
  }
];

/** Group order for reward channels */
const CHANNEL_GROUP_ORDER: RewardChannel[] = ['in_store', 'both', 'ecom'];

/** Group rewards by their rewardChannel */
function groupRewardsByChannel(
  rewards: UIReward[]
): { channel: RewardChannel; label: string; rewards: UIReward[] }[] {
  const grouped = new Map<RewardChannel, UIReward[]>();

  rewards.forEach((reward) => {
    const channel: RewardChannel = reward.rewardChannel || 'in_store';
    if (!grouped.has(channel)) {
      grouped.set(channel, []);
    }
    grouped.get(channel)!.push(reward);
  });

  return CHANNEL_GROUP_ORDER.filter((ch) => grouped.has(ch)).map((channel) => ({
    channel,
    label: REWARD_CHANNEL_LABELS[channel].replace(' ONLY', ''),
    rewards: grouped.get(channel)!
  }));
}
export default function MyBrandRewards() {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  // Get currentTierColor from navigation state
  const currentTierColor = (location.state as { currentTierColor?: string })
    ?.currentTierColor;

  const selectedCategory = categories[selectedTabIndex];

  const {
    data: rewardsRes,
    isLoading,
    error
  } = useQuery({
    queryKey: ['getRewards', params.id, selectedCategory.value],
    queryFn: () => getRewards(params.id || '', selectedCategory.value),
    enabled: !!params.id,
    refetchOnWindowFocus: false
  });

  // Group rewards by channel
  const rewardGroups = useMemo(() => {
    if (!rewardsRes?.success || !rewardsRes?.data?.rewards?.length) return [];
    return groupRewardsByChannel(rewardsRes.data.rewards);
  }, [rewardsRes]);

  // Skeleton loader component
  const RewardSkeleton = () => (
    <div className="animate-pulse rounded-lg border border-gray-200 p-4">
      <div className="flex items-center space-x-4">
        <div className="h-16 w-16 rounded-lg bg-gray-300"></div>
        <div className="flex-1 space-y-2">
          <div className="h-4 w-3/4 rounded bg-gray-300"></div>
          <div className="h-3 w-1/2 rounded bg-gray-300"></div>
          <div className="h-3 w-1/4 rounded bg-gray-300"></div>
        </div>
      </div>
    </div>
  );

  // Error state component
  const ErrorState = () => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="mb-4 text-4xl">⚠️</div>
      <h3 className="mb-2 text-lg font-medium text-gray-900">
        Something went wrong
      </h3>
      <p className="text-gray-500">
        Unable to load rewards. Please try again later.
      </p>
    </div>
  );

  // Empty state component
  const EmptyState = ({ category }: { category: string }) => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="mb-4 text-4xl">🎁</div>
      <h3 className="mb-2 text-lg font-medium text-gray-900">
        No {category.toLowerCase()} rewards found
      </h3>
      <p className="text-gray-500">
        {category === 'Active'
          ? 'Check back later for new rewards!'
          : "You haven't earned any rewards yet."}
      </p>
    </div>
  );

  return (
    <section className="flex flex-col gap-y-8">
      <div className={'flex flex-col gap-y-4'}>
        <SectionHeader
          title={
            <>
              <h1 className="text-lg font-medium leading-6 text-gray-900">
                My 🧡 Rewards
              </h1>
            </>
          }
        />

        <TabGroup
          className={'flex flex-col gap-y-2'}
          onChange={(index) => {
            setSelectedTabIndex(index);
          }}
        >
          <TabList className="mx-4 flex gap-x-4">
            {categories.map(({ name }) => (
              <Tab
                key={name}
                className="rounded-none px-2 py-1 text-sm/6 font-semibold focus:outline-none data-[selected]:border-b-2 data-[selected]:border-primary data-[selected]:text-primary"
              >
                {name}
              </Tab>
            ))}
          </TabList>
          <TabPanels className={'flex flex-col p-4'}>
            {categories.map(({ name, value }) => (
              <TabPanel
                key={value}
                as={'div'}
                className="flex flex-col gap-y-3 px-2"
              >
                {error ? (
                  <ErrorState />
                ) : isLoading ? (
                  // Show skeleton loaders while loading
                  Array.from({ length: 3 }).map((_, index) => (
                    <RewardSkeleton key={index} />
                  ))
                ) : rewardGroups.length > 0 ? (
                  // Show rewards grouped by channel
                  <div className="flex flex-col gap-y-8">
                    {rewardGroups.map((group) => (
                      <Disclosure key={group.channel} defaultOpen as="div">
                        <DisclosureButton className="group flex w-full items-center justify-between py-1">
                          <h3 className="text-xs font-bold uppercase tracking-wide text-gray-900">
                            {group.label}
                          </h3>
                          <ChevronDown className="h-4 w-4 text-gray-500 transition-transform duration-200 group-data-[open]:rotate-180" />
                        </DisclosureButton>
                        <DisclosurePanel className="flex flex-col gap-y-3 pt-2">
                          {group.rewards.map((reward) => (
                            <div key={reward.id}>
                              <RewardCard
                                merchantName={
                                  params.id === 'aye'
                                    ? 'Aye'
                                    : rewardsRes?.success
                                      ? rewardsRes?.data?.entity_name || ''
                                      : ''
                                }
                                {...reward}
                                showTnc
                                color={currentTierColor}
                              />
                            </div>
                          ))}
                        </DisclosurePanel>
                      </Disclosure>
                    ))}
                  </div>
                ) : (
                  // Show empty state if no rewards
                  <EmptyState category={name} />
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </TabGroup>
      </div>
    </section>
  );
}
